import React, { Suspense, useContext, useRef, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import NavHeader from './Componant/NavBar';
import { AuthContext, LoaderProvider } from './Context/ContextProvider';
import Loader from './Componant/Loader/Loader';
import './App.css';
import ScrollToTop from './website/components/common/ScrollToTop';
import UnsubscribePage from './website/pages/SubscriptionVerificationWindow';

// Eagerly Loaded Pages
import LandingPage from './website/pages/LandingPage';
import LandingPageTwo from './website/pages/LandingPageTwo';
import EmployerPage from './website/pages/Employers/Employer';
import JobSeeker from './website/pages/Home/index';
import CareerCentersPage from './website/pages/CareerCenters/CareerPartners';
import AboutUs from './website/pages/AboutUs';
import Login from './Admin/Login/Login';
import SignUp from './Admin/Login/SignUp';
import ForgetPassword from './Admin/Login/ForgetPassword';
import RestPassword from './Admin/Login/ResetPassWord';
import AutoLogin from './Admin/Login/AuthLogin';
import VerifySeeker from './Admin/Login/VerifySeeker';
import TermsOfService from './website/pages/Legal/TermsOfService';
import PrivacyPolicy from './website/pages/Legal/PrivacyPolicy';
import SubscriptionPage from './Componant/SubscriptionPage';
import SubscriptionSuccessPage from './Componant/SubscriptionSuccessPage';
import JobLead from './website/pages/JobLead';
import AnalyticsTracker from './Componant/AnalyticsTracker';
import { buttonBaseClasses } from '@mui/material';
import { Button } from 'bootstrap';
import { JobType } from './Admin/middleware/Enum';
import { get_student_profile } from './Services/Student/StudentApi';
import useSeekyToken from './hooks/useSeekyToken';
import { chatbotOrigin, chatbotUrl, isChatbotMessage, isSeekyV2Enabled } from './utils/seekyConfig';

const CHUNK_RELOAD_SESSION_KEY = 'seekjobs:chunk-load-reload-at';
const CHUNK_RELOAD_RETRY_WINDOW_MS = 60 * 1000;

const isChunkLoadError = error => {
  const message = String(error?.message || error || '');
  const name = String(error?.name || '');
  return (
    name === 'ChunkLoadError' ||
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message)
  );
};

const reloadForFreshChunks = () => {
  const now = Date.now();
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) || 0);

  if (Number.isFinite(lastReloadAt) && now - lastReloadAt < CHUNK_RELOAD_RETRY_WINDOW_MS) {
    return false;
  }

  sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(now));
  window.location.reload();
  return true;
};

const lazyWithChunkReload = importer =>
  React.lazy(() =>
    importer().catch(error => {
      if (isChunkLoadError(error) && reloadForFreshChunks()) {
        return new Promise(() => {});
      }

      throw error;
    }),
  );

// Lazy Loaded Pages
const Dashboard = lazyWithChunkReload(() => import('./Admin/Dashboard/Dashboard'));
const StudentList = lazyWithChunkReload(() => import('./Admin/Student/StudentList'));
const StudentDetails = lazyWithChunkReload(() => import('./Admin/Student/StudentDetailsPage'));
const JobList = lazyWithChunkReload(() => import('./Admin/Job/JobList'));
const ManageStudent = lazyWithChunkReload(() => import('./Admin/Student/ManageStudent'));
const StudentDashboard = lazyWithChunkReload(() => import('./StudentPanel/Dashboard/StudentDashboard'));
const FilterConceptDemo = lazyWithChunkReload(() => import('./StudentPanel/Dashboard/FilterConceptDemo'));
const StudentPanelStudentDetails = lazyWithChunkReload(() => import('./StudentPanel/ApplyJob/StudentPanelStudentDetails'));
const Insights = lazyWithChunkReload(() => import('./StudentPanel/Insights/Insights'));
const StudentEvents = lazyWithChunkReload(() => import('./StudentPanel/Events/Events'));
const UniversityEvents = lazyWithChunkReload(() => import('./Admin/Events/Events'));
const EmploymentReports = lazyWithChunkReload(() => import('./Admin/Employment/EmploymentReports'));
const EmploymentReport = lazyWithChunkReload(() => import('./StudentPanel/EmploymentReport/EmploymentReport'));
const EmployerDashboard = lazyWithChunkReload(() => import('./Employer/Dashboard/EmployerDashboard'));
const EmployerJobList = lazyWithChunkReload(() => import('./Employer/Job/JobList'));
const CandidateList = lazyWithChunkReload(() => import('./Employer/Candidate/CandidateList'));
const CandidateDetails = lazyWithChunkReload(() => import('./Employer/Candidate/CandidateDetails'));
const JobApplicants = lazyWithChunkReload(() => import('./Employer/Candidate/JobApplicants'));
const ManagePartnerships = lazyWithChunkReload(() => import('./Employer/University/ManagePartnerships'));
const PlatformAdminDashboard = lazyWithChunkReload(() => import('./PlatformAdmin/Dashboard/PlatformAdminDashboard'));
const AutofillPage = lazyWithChunkReload(() => import('./website/pages/Autofill/AutofillPage'));

// Signed-out visitors opening a shared /events link sign in, then return to that same path.
const RedirectToLoginWithReturn = () => {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(returnTo)}`} replace />;
};

const iframeStyleBase = {
  position: 'fixed',
  right: '20px',
  bottom: '20px',
  border: 'none',
  zIndex: 9999,
  backgroundColor: 'transparent',
  transition: 'width 0.3s ease, height 0.3s ease, max-width 0.3s ease, max-height 0.3s ease',
};

const iframeStyleClosed = {
  ...iframeStyleBase,
  width: '80px',
  height: '80px',
};

const iframeStyleOpen = {
  ...iframeStyleBase,
  width: '100%',
  maxWidth: '620px',
  top: '80px',
  height: 'calc(100% - 80px)',
  maxHeight: 'calc(100% - 80px)',
  borderRadius: '20px 0 0 0', // Top-left rounded heavily
  overflow: 'hidden', // Extremely important for iframes to respect border-radius!
  right: '0px',
  zIndex: 9999,
  pointerEvents: 'auto',
  onclick: null,
};
const isChatbotFeatureEnabled = process.env.REACT_APP_CHATBOT_ENABLED === 'true';
const STUDENT_DASHBOARD_PATHS = new Set(['/', '/student-dashboard', '/seeker-dashboard']);

const ChatbotWidget = ({ showChatbotBase, isChatbotOpen, setIsChatbotOpen, chatbotIframeRef, chatbotUrl, onLoad }) => {
  const location = useLocation();
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const isStudentDashboardRoute = STUDENT_DASHBOARD_PATHS.has(location.pathname);
  const shouldShowChatbot = showChatbotBase && isStudentDashboardRoute && !isProfilePanelOpen;

  useEffect(() => {
    const offcanvas = document.getElementById('editProfileCanvas');
    if (!offcanvas) {
      setIsProfilePanelOpen(false);
      return;
    }

    const updatePanelState = () => {
      setIsProfilePanelOpen(offcanvas.classList.contains('show'));
    };

    updatePanelState();
    offcanvas.addEventListener('shown.bs.offcanvas', updatePanelState);
    offcanvas.addEventListener('hidden.bs.offcanvas', updatePanelState);

    const classObserver = new MutationObserver(updatePanelState);
    classObserver.observe(offcanvas, { attributes: true, attributeFilter: ['class'] });

    return () => {
      offcanvas.removeEventListener('shown.bs.offcanvas', updatePanelState);
      offcanvas.removeEventListener('hidden.bs.offcanvas', updatePanelState);
      classObserver.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldShowChatbot && isChatbotOpen) {
      setIsChatbotOpen(false);
      // Explicitly tell the iframe to collapse its own state since we are hiding it
      const iframe = chatbotIframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Send FORCE_CLOSE to trigger internal reset inside the chatbot
        iframe.contentWindow.postMessage({ type: 'FORCE_CLOSE' }, chatbotOrigin);
      }
    }
  }, [shouldShowChatbot, isChatbotOpen, setIsChatbotOpen, chatbotUrl, chatbotIframeRef]);

  useEffect(() => {
    if (!isChatbotOpen || !shouldShowChatbot) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isChatbotOpen, shouldShowChatbot]);

  // Keep the component mounted, just hide it using visibility/opacity so the iframe doesn't break
  const finalIframeStyle = {
    ...(isChatbotOpen ? iframeStyleOpen : iframeStyleClosed),
    visibility: shouldShowChatbot ? 'visible' : 'hidden',
    opacity: shouldShowChatbot ? 1 : 0,
    pointerEvents: shouldShowChatbot ? 'auto' : 'none',
    transition: 'opacity 0.3s ease, visibility 0.3s ease, width 0.3s ease, height 0.3s ease',
    // Extremely important: make sure when it returns to view, the z-index is restored properly
    zIndex: shouldShowChatbot ? 9999 : -1,
  };

  return (
    <>
      {isChatbotOpen && shouldShowChatbot && (
        <div
          className="chatbot-overlay"
          onClick={() => {
            setIsChatbotOpen(false);
            const iframe = chatbotIframeRef.current;
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage({ type: 'FORCE_CLOSE' }, chatbotOrigin);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 'calc(100% - 620px)',
            height: '100vh',
            cursor: 'pointer',
            zIndex: 9998,
          }}
        />
      )}
      <iframe
        ref={chatbotIframeRef}
        id="chatbot-iframe"
        src={chatbotUrl}
        onLoad={onLoad}
        style={finalIframeStyle}
        title="AI Chatbot Assistant"
        onClick={() => {
          window.dispatchEvent(new Event('seekjobs:close-profile-dropdown'));
          if (!isChatbotOpen) setIsChatbotOpen(true);
        }}
      />
    </>
  );
};

function App() {
  const { isLoggedIn, userRole, userId } = useContext(AuthContext);
  const scrollRef = useRef(null);
  const chatbotIframeRef = useRef(null);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isChatbotReady, setIsChatbotReady] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOptions, setFilterOptions] = useState({});
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(isLoggedIn);
  const [chatbotReady, setChatbotReady] = useState(false);
  const [chatbotloading, setChatbotLoading] = useState(false);
  const [lastChatbotResponse, setLastChatbotResponse] = useState('');
  const [showChatbotNotification, setShowChatbotNotification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Determine if chatbot should be visible (Only for logged-in Students or Seekers)
  const showChatbotBase = isChatbotFeatureEnabled && isLoggedIn && (userRole === 'student_registered' || userRole === 'seeker');

  const [filters, setFilters] = useState({
    jobTitle: '',
    location: '',
    salaryRange: [0, 200000],
    experienceLevel: '',
    datePosted: '',
    JobTypes: '',
  });

  const handleSeekyLoad = useSeekyToken({
    iframeRef: chatbotIframeRef,
    enabled: isChatbotFeatureEnabled && isSeekyV2Enabled,
    isLoggedIn,
    userId,
    userRole,
  });

  useEffect(() => {
    const handleMessage = event => {
      // Security: Now using the environment variable for the origin check
      if (!isChatbotMessage(event, chatbotIframeRef.current)) return;

      // if (event.data && event.data.type === 'CHATBOT_STATE') {
      //   console.log('[Main App] Message RECEIVED from iframe. New state:', event.data.isOpen);
      //   setIsChatbotOpen(event.data.isOpen);
      // }
      // if (event.data && event.data.type === 'CHATBOT_READY') {
      //   console.log('Parent received CHATBOT_READY signal.');
      //   setIsChatbotReady(true);
      // }

      switch (event.data.type) {
        case 'CHATBOT_STATE':
          if (typeof event.data.isOpen === 'boolean') setIsChatbotOpen(event.data.isOpen);
          break;
        case 'CHATBOT_RESPONSE':
          // The dashboard applies V2's explicit filter_update. It owns the
          // filter state and API request; no response-text inference is needed.
          if (isSeekyV2Enabled) break;
          handleChatbotResponse(event.data.response, event.data.userMessage, event.data.userProfile);
          break;
        case 'CHATBOT_ERROR':
          console.error('Chatbot error:', event.data.error);
          alert(`Chatbot error: ${event.data.error}`);
          break;
        case 'CLEAR_FILTERS':
          if (!isSeekyV2Enabled) clearAllFilters();
          break;
        case 'REQUEST_CURRENT_STATE':
          if (!isSeekyV2Enabled) sendCurrentStateToChatbot();
          break;
        case 'REQUEST_JOB_DATA':
          if (isSeekyV2Enabled) break;
          const iframe = chatbotIframeRef.current;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: 'JOB_DATA_RESPONSE',
                jobs: allJobs.slice(0, 50),
                availableFilters: filterOptions,
                user: user,
                currentFilters: filters,
                searchQuery: searchQuery,
              },
              chatbotOrigin,
            );
          }
          break;
        // case 'CHATBOT_READY':
        //   console.log('Chatbot is ready');
        //   setChatbotReady(true);
        //   setChatbotLoading(false);
        //   // Send user profile to chatbot when it's ready
        //   if (isAuthenticated && user) {
        //     const iframe = document.querySelector('.chatbot-iframe');
        //     if (iframe && iframe.contentWindow) {
        //       iframe.contentWindow.postMessage(
        //         {
        //           type: 'USER_PROFILE',
        //           profile: user,
        //         },
        //         '*',
        //       );
        //       console.log('📤 Sent user profile to chatbot');
        //     }
        //   }
        //   break;

        case 'CHATBOT_READY':
          setIsChatbotReady(true);
          break;

        case 'SEEKY_TOKEN_REQUEST':
          // The token hook answers both startup and refresh messages.
          break;

        case 'FORCE_CLOSE':
          setIsChatbotOpen(false);
          break;

        case 'CHAT_SESSION_CLEARED':
          break;

        default:
          console.log('Unknown message type:', event.data.type);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [filters, allJobs, user, searchQuery, filterOptions, chatbotOpen, isAuthenticated]);

  useEffect(() => {
    const handleCloseChatbot = () => {
      setIsChatbotOpen(false);
      const iframe = chatbotIframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'FORCE_CLOSE' }, chatbotOrigin);
      }
    };

    window.addEventListener('seekjobs:close-chatbot', handleCloseChatbot);
    return () => window.removeEventListener('seekjobs:close-chatbot', handleCloseChatbot);
  }, []);

  //   };
  //   window.addEventListener('message', handleMessage);
  //   return () => window.removeEventListener('message', handleMessage);
  // }, [chatbotOrigin]);
  // Enhanced chatbot response handler

  const handleChatbotResponse = (response, userMessage, userProfile) => {
    console.log('🤖 Processing chatbot response:', response);
    console.log('💬 User message was:', userMessage);
    console.log('👤 User profile context:', userProfile);

    setLastChatbotResponse(response);

    // Check if this is a filter update
    if (response === 'filter_update' && userProfile && userProfile.filters) {
      console.log('🔄 Processing filter update from chatbot');
      handleFilterUpdateFromChatbot(userProfile.filters);
      return; // Don't process as regular response
    }

    // Show notification for regular responses
    displayChatbotNotification(response);

    // Auto-process chatbot suggestions for regular responses
    processChatbotResponse(response, userMessage);
  };

  const processChatbotResponse = (response, userMessage) => {
    const lowerResponse = response.toLowerCase();
    const lowerUserMessage = userMessage.toLowerCase();
    // Auto-apply location filters
    if (lowerResponse.includes('remote') && !filters.location.includes('remote')) {
      console.log('🤖 Auto-applying remote filter');
      handleFilterChange('location', 'remote');
      setTimeout(() => applyFilters({ ...filters, location: 'remote' }), 500);
    }

    // Auto-apply job type filters
    if (lowerResponse.includes('full-time') && !filters.jobTypes.includes('full-time')) {
      handleFilterChange('jobTypes', 'Full-time');
      setTimeout(() => applyFilters({ ...filters, jobTypes: 'Full-time' }), 500);
    }

    if (lowerResponse.includes('part-time') && !filters.jobTypes.includes('part-time')) {
      handleFilterChange('jobTypes', 'Part-time');
      setTimeout(() => applyFilters({ ...filters, jobTypes: 'Part-time' }), 500);
    }

    // Auto-search based on user message
    if ((lowerResponse.includes('search') || lowerResponse.includes('find') || lowerResponse.includes('looking')) && userMessage) {
      const searchTerms = extractSearchTerms(userMessage);
      if (searchTerms) {
        console.log('🤖 Auto-searching for:', searchTerms);
        setSearchQuery(searchTerms);
        setTimeout(() => handleSearch(), 1000);
      }
    }

    // Clear filters suggestion
    if (lowerResponse.includes('clear') && lowerResponse.includes('filter')) {
      setTimeout(() => {
        if (window.confirm('Chatbot suggests clearing filters. Would you like to clear all filters?')) {
          clearAllFilters();
        }
      }, 1000);
    }

    // Experience level filters
    if (lowerResponse.includes('entry level') && !filters.experienceLevels.includes('entry')) {
      handleFilterChange('experienceLevels', 'Entry Level');
      setTimeout(() => applyFilters({ ...filters, experienceLevels: 'Entry Level' }), 500);
    }

    if (lowerResponse.includes('senior') && !filters.experienceLevels.includes('senior')) {
      handleFilterChange('experienceLevels', 'Senior Level');
      setTimeout(() => applyFilters({ ...filters, experienceLevels: 'Senior Level' }), 500);
    }
  };

  const handleFilterUpdateFromChatbot = newFilters => {
    console.log('🔄 Applying filters from chatbot:', newFilters);

    // Map the chatbot filters to your app's filter structure
    const mappedFilters = {
      jobTitle: newFilters.jobTitle && newFilters.jobTitle.length > 0 ? newFilters.jobTitle[0] : '',
      jobTypes: newFilters.jobTypes && newFilters.jobTypes.length > 0 ? newFilters.jobTypes[0] : '',
      location:
        newFilters.location && newFilters.location.cities && newFilters.location.cities.length > 0
          ? newFilters.location.cities[0]
          : newFilters.location && newFilters.location.remote
            ? 'remote'
            : '',
      experienceLevels: newFilters.experienceLevels && newFilters.experienceLevels.length > 0 ? newFilters.experienceLevels[0] : '',
      datePosted: newFilters.datePosted || '',
      workAuthorization: newFilters.workAuthorization || false,
    };

    console.log('Mapped filters:', mappedFilters);

    // Update the filters state
    setFilters(mappedFilters);

    // Apply the filters
    applyFilters(mappedFilters, 1);

    // Show success notification
    setLastChatbotResponse('Dashboard filters updated successfully!');
    setShowChatbotNotification(true);
    setTimeout(() => setShowChatbotNotification(false), 3000);
  };

  // Apply filters and fetch from API
  const applyFilters = async (newFilters = {}, page = 1) => {
    console.log('Applying filters:', newFilters);

    const searchParams = {
      query: searchQuery || '',
      filters: newFilters,
      page: page,
    };

    console.log('API Request:', searchParams);
    // await fetchJobs(searchParams);
  };

  // // Enhanced fetch jobs function with better response handling
  // const fetchJobs = async (searchParams = {}) => {
  //   setLoading(true);
  //   try {
  //     const response = await jobService.fetchJobs(searchParams);

  //     console.log('Frontend received jobs response:', response);

  //     // Handle response according to API spec
  //     let jobsData = [];
  //     if (response && response.jobs) {
  //       jobsData = Array.isArray(response.jobs) ? response.jobs : [];
  //     } else if (response && Array.isArray(response)) {
  //       jobsData = response;
  //     } else if (Array.isArray(response)) {
  //       jobsData = response;
  //     }

  //     console.log('Processed jobs data:', jobsData);
  //     console.log('Number of jobs found:', jobsData.length);

  //     setAllJobs(jobsData);
  //     setJobs(jobsData);
  //     setCurrentPage(response.page || searchParams.page || 1);
  //     setTotalPages(response.totalPages || response.total_pages || 1);

  //     // Update filter options with new job data
  //     updateFilterOptions(jobsData);

  //     // Select first job if available
  //     if (jobsData.length > 0 && !selectedJob) {
  //       setSelectedJob(jobsData[0]);
  //       console.log('Auto-selected first job:', jobsData[0]);
  //     } else if (jobsData.length === 0) {
  //       setSelectedJob(null);
  //       console.log('No jobs available to select');
  //     }

  //     // Notify chatbot of new job data
  //     if (chatbotOpen) {
  //       sendCurrentStateToChatbot();
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch jobs:', error);
  //     // Check if it's an authentication error
  //     if (error.message.includes('401') || error.message.includes('unauthorized')) {
  //       setIsAuthenticated(false);
  //       setUser(null);
  //     }
  //     setAllJobs([]);
  //     setJobs([]);
  //     setSelectedJob(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    if (isSeekyV2Enabled) return;
    const iframe = chatbotIframeRef.current;
    if (!iframe || !iframe.contentWindow || !isChatbotReady) {
      return;
    }

    const initializeChatbot = async () => {
      console.log('[Main App] Sending message TO iframe. isLoggedIn:', isLoggedIn, 'userId:', userId);
      if (isLoggedIn && userId) {
        let userProfile = JSON.parse(localStorage.getItem('login') || '{}');
        let profile = userProfile.student || userProfile.seeker || {};
        let subscription_tier = (profile.subscription_tier || 'free').toLowerCase();

        // Fetch latest profile if subscription_tier is missing or 'free'
        if (subscription_tier === 'free') {
          try {
            const res = await get_student_profile();
            if (res && res.profile) {
              profile = res.profile;
              subscription_tier = (profile.subscription_tier || 'free').toLowerCase();
            }
          } catch (error) {
            console.error('Error fetching student profile for chatbot:', error);
          }
        }

        console.log(`Sending userId (${userId}) to chatbot iframe...(${subscription_tier})...`);
        iframe.contentWindow.postMessage(
          {
            type: 'USER_ID',
            userId: userId,
            subscription_tier: subscription_tier.toLowerCase(),
          },
          chatbotOrigin,
        );

        // Send user profile if available
        if (profile) {
          iframe.contentWindow.postMessage(
            {
              type: 'USER_PROFILE',
              profile: profile,
              subscription_tier: subscription_tier.toLowerCase(),
            },
            chatbotOrigin,
          );
        }
      } else {
        console.log('Sending logout signal (null userId) to chatbot iframe...');
        iframe.contentWindow.postMessage(
          {
            type: 'USER_LOGOUT',
          },
          chatbotOrigin, // Use origin (e.g., https://site.com) instead of full URL
        );
      }
    };

    initializeChatbot();
  }, [isLoggedIn, userId, isChatbotReady, chatbotUrl]);

  return (
    <Router>
      <div className="page-wrapper compact-wrapper" id="pageWrapper">
        <analyticstracker />
        <LoaderProvider>
          {isLoggedIn && <NavHeader />}
          <div ref={scrollRef}>
            <ScrollToTop />
            <Suspense fallback={<Loader />}>
              <Routes>
                {!isLoggedIn && <Route path="/" element={<LandingPage />} />}
                {!isLoggedIn && <Route path="/features" element={<LandingPageTwo />} />}
                {!isLoggedIn && <Route path="/employer" element={<EmployerPage />} />}
                {!isLoggedIn && <Route path="/career-centers" element={<CareerCentersPage />} />}
                {!isLoggedIn && <Route path="/job-seeker" element={<JobSeeker />} />}
                {!isLoggedIn && <Route path="/about-us" element={<AboutUs />} />}
                <Route path="/login" element={<Login />} />
                {!isLoggedIn && <Route path="/sign-up" element={<SignUp />} />}
                {!isLoggedIn && <Route path="/forget-password" element={<ForgetPassword />} />}
                {!isLoggedIn && <Route path="/reset-password" element={<RestPassword />} />}
                {!isLoggedIn && <Route path="/autologin" element={<AutoLogin />} />}
                {!isLoggedIn && <Route path="/verify-seeker" element={<VerifySeeker />} />}
                {!isLoggedIn && <Route path="/events/*" element={<RedirectToLoginWithReturn />} />}
                <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
                <Route path="/job-lead" element={<JobLead />} />
                <Route path="/filter-concepts-demo" element={<FilterConceptDemo />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/autofill" element={<AutofillPage />} />
                {isLoggedIn && userRole === 'organization' && (
                  <>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/students" element={<StudentList />} />
                    <Route path="/students_details" element={<StudentDetails />} />
                    <Route path="/job" element={<JobList />} />
                    <Route path="/manage_students" element={<ManageStudent />} />
                    <Route path="/events/*" element={<UniversityEvents />} />
                    <Route path="/employment" element={<EmploymentReports />} />
                  </>
                )}
                {isLoggedIn && userRole === 'student_registered' && (
                  <>
                    <Route path="/" element={<StudentDashboard />} />
                    <Route path="/student-dashboard" element={<StudentDashboard />} />
                    <Route path="/report-employment" element={<EmploymentReport />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/events/*" element={<StudentEvents />} />
                    <Route path="/student/apply-job/:viewId" element={<StudentPanelStudentDetails />} />
                  </>
                )}
                {isLoggedIn && userRole === 'seeker' && (
                  <>
                    <Route path="/" element={<StudentDashboard />} />
                    <Route path="/seeker-dashboard" element={<StudentDashboard />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/student/apply-job/:viewId" element={<StudentPanelStudentDetails />} />
                    <Route path="/subscription" element={<SubscriptionPage />} />
                    <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
                  </>
                )}
                {isLoggedIn && userRole === 'employer' && (
                  <>
                    <Route path="/" element={<EmployerDashboard />} />
                    <Route path="/employer/dashboard" element={<EmployerDashboard />} />
                    <Route path="/employer/jobs" element={<EmployerJobList />} />
                    <Route path="/employer/jobs/:jobId/applicants" element={<JobApplicants />} />
                    <Route path="/employer/candidates" element={<CandidateList />} />
                    <Route path="/employer/candidate-details" element={<CandidateDetails />} />
                    <Route path="/employer/partnerships" element={<ManagePartnerships />} />
                  </>
                )}
                {isLoggedIn && userRole === 'admin' && (
                  <>
                    <Route path="/" element={<PlatformAdminDashboard />} />
                    <Route path="/admin-dashboard" element={<PlatformAdminDashboard />} />
                  </>
                )}
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={
                        isLoggedIn
                          ? userRole === 'organization'
                            ? '/dashboard'
                            : userRole === 'student_registered'
                              ? '/student-dashboard'
                              : userRole === 'seeker'
                                ? '/seeker-dashboard'
                                : userRole === 'employer'
                                  ? '/employer/dashboard'
                                  : userRole === 'admin'
                                    ? '/admin-dashboard'
                                    : '/'
                          : '/'
                      }
                      replace
                    />
                  }
                />
              </Routes>
            </Suspense>
          </div>
          {isChatbotFeatureEnabled && chatbotOrigin && chatbotUrl && <ChatbotWidget
            showChatbotBase={showChatbotBase}
            isChatbotOpen={isChatbotOpen}
            setIsChatbotOpen={setIsChatbotOpen}
            chatbotIframeRef={chatbotIframeRef}
            chatbotUrl={chatbotUrl}
            onLoad={handleSeekyLoad}
          />}
        </LoaderProvider>
      </div>
    </Router>
  );
}

export default App;

